/**
 * Output render model
 *
 * A pure-data, output-agnostic description of the estimate output tables (BOM
 * and Product Placement Guide). Built once per render from the resolved column
 * config + the BOM/surface data, then consumed by:
 *   - the HTML renderer (src/components/results/RenderTable.tsx) — browser & print
 *   - the PDF renderer  (src/utils/pdf.ts, drawRenderTable)
 *
 * Keeping a single descriptor is what stops the three output surfaces (browser,
 * @media print, hand-drawn jsPDF) from drifting apart as columns become
 * configurable. Phase 1 reproduces today's hardcoded column layout exactly; the
 * Column Layout Designer (Phase 1.3+) and dynamic/barcode columns (Phase 2)
 * extend this model without touching the renderers.
 *
 * i18n: built-in column headings are resolved here, once, so every surface —
 * including the CSV export — gets the same translated heading. This module is
 * not a component, so it reads the live i18next instance via `tr` (see
 * src/i18n/standalone.ts); the components that render these models subscribe
 * with useTranslation() and so rebuild them on a language switch.
 *
 * Admin-authored heading overrides are NOT translated here — they are database
 * text and belong to the content_translations overlay (Phase 3).
 */

import {
  CellBlock, CellBlockType, CellStyle, ColumnLayout, ColumnLayoutItem, ColumnLayoutScope,
  CustomFieldColumnConfig, CustomTableCard, CustomTableCell, CustomTableDef, Currency,
  EstimateOutput, MaterialItem, OutputLayoutConfig, ProductCustomField,
} from '../types';
import { buildSkuColumns, SkuColumn } from './customFieldColumns';
import { findProductById } from '../data/productTable';
import { buildBarcodeUrl } from './barcode';
import { tr } from '../i18n/standalone';
import type { ProductLinkAnalytics, ProductLinkSection } from './gtmTracking';

export type CellAlign = 'left' | 'center' | 'right';

/**
 * Built-in GS1 product codes selectable as a barcode/column source in the
 * Column Layout & Custom Tables designers (alongside 'productNumber' and any
 * custom field). Kept here so the designers and the resolvers below stay in
 * sync. gs1Prefix is token-only (not a scannable barcode on its own).
 *
 * Labels stay English on purpose: they are GS1 standard identifiers, not prose,
 * and they appear in the admin designers, which are English by scope.
 */
export const BUILTIN_CODE_SOURCES: { key: string; label: string }[] = [
  { key: 'upc', label: 'UPC (GTIN-12)' },
  { key: 'gtin', label: 'GTIN (14-digit)' },
  { key: 'ean', label: 'EAN (GTIN-13)' },
  { key: 'gtin8', label: 'GTIN-8' },
];
/** Keys valid as a barcode source (scannable codes). */
const CODE_SOURCE_KEYS = new Set(BUILTIN_CODE_SOURCES.map((c) => c.key));
/** Keys valid as a {token} (adds the non-scannable company prefix). */
const CODE_TOKEN_KEYS = new Set([...CODE_SOURCE_KEYS, 'gs1Prefix']);

/** Resolve a built-in GS1 code off the hydrated product, or '' if absent. */
function resolveProductCode(key: string, productID: string): string {
  const p = findProductById(productID);
  const v = p ? (p as unknown as Record<string, unknown>)[key] : null;
  return typeof v === 'string' ? v : '';
}

/** Output-agnostic kind of a column; drives default width/alignment in PDF. */
export type ColumnKind =
  | 'lineNo'
  | 'sku'
  | 'product'
  | 'qty'
  | 'checkbox'
  | 'linearFt'
  | 'totalSqft'
  | 'unitPrice'
  | 'total'
  // reserved for later phases:
  | 'custom'
  | 'barcode'
  | 'remote';

/** One row of source data feeding a materials table. */
export type MaterialRow = MaterialItem;

/** Everything a cell extractor needs that isn't on the row itself. */
export interface RenderCtx {
  currency: Currency;
  showPrices: boolean;
  /** Live custom-field value lookup for a product (see productTable). */
  getFields: (productID: string) => ProductCustomField[];
}

/** A resolved, output-agnostic cell value. */
export type RenderCell =
  | { type: 'text'; text: string }
  /** `analytics` rides along so the renderer can report the click without
   *  knowing anything about custom fields. */
  | { type: 'link'; text: string; href: string; analytics?: ProductLinkAnalytics }
  | { type: 'checkbox'; checked: boolean }
  | { type: 'barcode'; src: string; pngUrl: string; alt: string; heightPx?: number }
  | { type: 'empty' };

/** A single footer (totals) cell, pre-aligned to its column. */
export interface RenderFooterCell {
  cell: RenderCell;
  /** Exact HTML className for the <td> (preserves legacy styling). */
  className?: string;
}

/** Description of one column across all output targets. */
export interface RenderColumn {
  key: string;
  /** Stable identifier the Column Layout Designer reorders/toggles by:
   *  'builtin:<key>' | 'sku' | 'field:<fieldKey>'. */
  layoutId: string;
  heading: string;
  kind: ColumnKind;
  align: CellAlign;
  /** Render the checked checkbox icon as the heading (instead of text). */
  headingIcon?: 'checked';
  /** Exact HTML className for the <th> (preserves legacy styling). */
  thClassName?: string;
  /** Exact HTML className for each body <td> (preserves legacy styling). */
  tdClassName?: string;
  /** PDF column width weight; larger = wider. Defaults applied in pdf.ts. */
  widthHint?: number;
  visibleBrowser: boolean;
  visiblePrint: boolean;
  visiblePdf: boolean;
  /** Per-row cell extractor. */
  cell: (row: MaterialRow, ctx: RenderCtx) => RenderCell;
}

/** A complete table ready to render in any target. */
export interface RenderTableModel {
  /** Bootstrap-extending table class: 'materials-table' | 'placement-table'. */
  tableClassName: string;
  columns: RenderColumn[];
  rows: MaterialRow[];
  /** Totals row (BOM only); omitted for placement tables. */
  footer?: RenderFooterCell[];
  /** Which output surface this table is, for link-click analytics. */
  analyticsSection: ProductLinkSection;
}

// ---------------------------------------------------------------------------
// Formatting helpers (replicate the exact legacy output)
// ---------------------------------------------------------------------------

const currencySymbol = (currency: Currency): string => (currency === 'USD' ? '$' : 'C$');

function unitPriceText(productID: string, currency: Currency): string {
  const product = findProductById(productID);
  if (!product) return '-';
  const price = currency === 'USD' ? product.pricingUSD : product.pricingCAD;
  return `${currencySymbol(currency)}${price.toFixed(2)}`;
}

function totalPriceText(productID: string, quantity: number, currency: Currency): string {
  const product = findProductById(productID);
  if (!product) return '-';
  const price = currency === 'USD' ? product.pricingUSD : product.pricingCAD;
  const total = price * quantity;
  return `${currencySymbol(currency)}${total.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const linearFtText = (row: MaterialRow): string => (row.lengthFeet * row.quantity).toLocaleString();
const totalSqftText = (row: MaterialRow): string =>
  (row.sqft * row.quantity).toLocaleString(undefined, { maximumFractionDigits: 0 });

/** Resolve a SKU column to an output-agnostic cell (mirrors renderSkuCell). */
function skuCell(col: SkuColumn, row: MaterialRow, ctx: RenderCtx): RenderCell {
  if (col.isBuiltIn) return { type: 'text', text: row.productNumber };
  const field = ctx.getFields(row.productID).find((f) => f.fieldKey === col.key);
  const value = field?.value;
  if (value === null || value === undefined || value === '') {
    return { type: 'text', text: col.fallbackToProductNumber ? row.productNumber : '—' };
  }
  if (col.linkable && field?.resolvedUrl) {
    return {
      type: 'link',
      text: value,
      href: field.resolvedUrl,
      analytics: {
        fieldKey: col.key,
        storeSku: value,
        trusscoreSku: row.productNumber,
        productName: row.name,
        qty: row.quantity,
      },
    };
  }
  return { type: 'text', text: value };
}

// ---------------------------------------------------------------------------
// Column builders — Phase 1 reproduces today's hardcoded layout exactly.
// ---------------------------------------------------------------------------

const VISIBLE_ALL = { visibleBrowser: true, visiblePrint: true, visiblePdf: true } as const;

/** Pick the layout for a given table scope from a resolved OutputLayoutConfig. */
export function pickLayout(
  config: OutputLayoutConfig | null | undefined,
  scope: ColumnLayoutScope,
): ColumnLayout | undefined {
  return config?.layouts.find((l) => l.scope === scope);
}

// ---------------------------------------------------------------------------
// Custom output tables (block-based scan sheets)
// ---------------------------------------------------------------------------

export interface ResolvedBlock {
  type: CellBlockType;
  style?: CellStyle;
  text?: string;
  barcode?: { src: string; pngUrl: string; alt: string; heightPx?: number };
}
export interface ResolvedCell { colSpan: number; background?: string; blocks: ResolvedBlock[]; }
export interface ResolvedCard { cells: ResolvedCell[]; background?: string; }
export interface RenderBlockTable {
  key: string;
  title: string;
  placement: 'belowBom' | 'belowPlacement';
  cardsPerRow: number;
  cards: ResolvedCard[];
  visibleBrowser: boolean;
  visiblePrint: boolean;
  visiblePdf: boolean;
}

/** Resolve one {token} against a data item (null for static cards). */
function resolveToken(key: string, item: MaterialItem | null, ctx: RenderCtx): string {
  if (!item) return '';
  switch (key) {
    case 'productNumber': return item.productNumber;
    case 'name': return item.name;
    case 'qty': return String(item.quantity);
    case 'color': return item.color;
    case 'linearFt': return (item.lengthFeet * item.quantity).toLocaleString();
    case 'sqft': return (item.sqft * item.quantity).toLocaleString(undefined, { maximumFractionDigits: 0 });
    default: {
      if (CODE_TOKEN_KEYS.has(key)) return resolveProductCode(key, item.productID);
      const f = ctx.getFields(item.productID).find((x) => x.fieldKey === key);
      return f?.value ?? '';
    }
  }
}

function resolveTokens(text: string | undefined, item: MaterialItem | null, ctx: RenderCtx): string {
  if (!text) return '';
  return text.replace(/\{(\w+)\}/g, (_m, key: string) => resolveToken(key, item, ctx));
}

/** The value a barcode block encodes (built-in product number or a field). */
function resolveBarcodeSource(sourceFieldKey: string, item: MaterialItem | null, ctx: RenderCtx): string {
  if (!item) return '';
  if (sourceFieldKey === 'productNumber') return item.productNumber;
  if (CODE_SOURCE_KEYS.has(sourceFieldKey)) return resolveProductCode(sourceFieldKey, item.productID);
  return ctx.getFields(item.productID).find((f) => f.fieldKey === sourceFieldKey)?.value ?? '';
}

function resolveBlock(block: CellBlock, item: MaterialItem | null, ctx: RenderCtx): ResolvedBlock {
  switch (block.type) {
    case 'textLine':
      return { type: 'textLine', style: block.style, text: resolveTokens(block.text, item, ctx) };
    case 'qtyBadge':
      // Default to the quantity; admins can add a label, e.g. "Qty: {qty}".
      return { type: 'qtyBadge', style: block.style, text: resolveTokens(block.text || '{qty}', item, ctx) };
    case 'spacer':
      return { type: 'spacer', style: block.style };
    case 'barcode': {
      const cfg = block.barcode;
      if (!cfg) return { type: 'spacer', style: block.style };
      const value = resolveBarcodeSource(cfg.sourceFieldKey, item, ctx);
      if (!value) {
        // No value to encode → a small muted note in the barcode's place.
        // The field key itself is an admin-defined identifier and stays as-is;
        // only the sentence around it is translated.
        const label = cfg.sourceFieldKey === 'productNumber'
          ? tr('results:columns.productNumber')
          : cfg.sourceFieldKey;
        return {
          type: 'textLine',
          style: { align: block.style?.align ?? 'center', color: '#9ca3af', fontSizePx: 9 },
          text: tr('results:table.codeNotSet', { label }),
        };
      }
      return {
        type: 'barcode',
        style: block.style,
        barcode: {
          src: buildBarcodeUrl(value, cfg.symbology, 'svg', cfg.heightPx),
          pngUrl: buildBarcodeUrl(value, cfg.symbology, 'png', cfg.heightPx),
          alt: value,
          heightPx: cfg.heightPx,
        },
      };
    }
    default:
      return { type: 'spacer' };
  }
}

function resolveCard(card: CustomTableCard, item: MaterialItem | null, ctx: RenderCtx): ResolvedCard {
  return {
    background: card.background,
    cells: (card.cells ?? []).map((cell: CustomTableCell) => ({
      colSpan: cell.colSpan,
      background: cell.background,
      blocks: (cell.blocks ?? []).map((b) => resolveBlock(b, item, ctx)),
    })),
  };
}

/** Aggregate material items by productNumber (one card per SKU, summed qty). */
function aggregateItems(items: MaterialItem[]): MaterialItem[] {
  const map = new Map<string, MaterialItem>();
  items.forEach((it) => {
    const ex = map.get(it.productNumber);
    if (ex) ex.quantity += it.quantity;
    else map.set(it.productNumber, { ...it });
  });
  return Array.from(map.values());
}

/** Source data items for a table's dataSource. */
function dataItemsFor(def: CustomTableDef, estimate: EstimateOutput): MaterialItem[] {
  if (def.dataSource === 'bom') {
    const b = estimate.billOfMaterials;
    return aggregateItems([
      ...b.wallAndCeilingBoard, ...b.slatwall, ...b.hTrims, ...b.jTrimsHalfInch, ...b.jTrimsThreeQuarterInch,
    ]);
  }
  if (def.dataSource === 'surfaces') {
    const items: MaterialItem[] = [];
    const s = estimate.surfaces;
    if (s.ceiling) items.push(...s.ceiling.materials.panels, ...s.ceiling.materials.trims);
    (s.ceilingZones ?? []).forEach((z) => items.push(...z.materials.panels, ...z.materials.trims));
    s.walls.forEach((w) => items.push(...w.materials.panels, ...w.materials.trims));
    return aggregateItems(items);
  }
  return [];
}

/**
 * Resolve custom-table definitions into render-ready block tables for an
 * estimate. dataSource bom/surfaces repeat the card template per SKU; 'static'
 * uses staticCards verbatim.
 */
export function buildCustomTables(
  defs: CustomTableDef[] | undefined,
  estimate: EstimateOutput,
  ctx: RenderCtx,
  placement: 'belowBom' | 'belowPlacement',
): RenderBlockTable[] {
  return (defs ?? [])
    .filter((d) => d.placement === placement && (d.isActive ?? true))
    .map((def) => {
      let cards: ResolvedCard[];
      if (def.dataSource === 'static') {
        cards = (def.staticCards ?? []).map((c) => resolveCard(c, null, ctx));
      } else {
        cards = dataItemsFor(def, estimate).map((item) => resolveCard(def.card, item, ctx));
      }
      return {
        key: def.key,
        title: def.title,
        placement: def.placement,
        cardsPerRow: Math.max(1, Math.min(4, def.cardsPerRow || 2)),
        cards,
        visibleBrowser: def.visibleBrowser,
        visiblePrint: def.visiblePrint,
        visiblePdf: def.visiblePdf,
      };
    });
}

/** Build a single block table from explicit sample items (Designer preview). */
export function buildCustomTablePreview(
  def: CustomTableDef,
  sampleItems: MaterialItem[],
  ctx: RenderCtx,
): RenderBlockTable {
  const cards = def.dataSource === 'static'
    ? (def.staticCards && def.staticCards.length ? def.staticCards : [def.card]).map((c) => resolveCard(c, null, ctx))
    : sampleItems.map((item) => resolveCard(def.card, item, ctx));
  return {
    key: def.key || 'preview',
    title: def.title,
    placement: def.placement,
    cardsPerRow: Math.max(1, Math.min(4, def.cardsPerRow || 2)),
    cards,
    visibleBrowser: true,
    visiblePrint: true,
    visiblePdf: true,
  };
}

/** Metadata about a candidate column, for the Column Layout Designer. */
export interface ColumnMeta {
  layoutId: string;
  heading: string;
  kind: ColumnKind;
  align: CellAlign;
}

/**
 * The full set of candidate columns for a table scope in their default order,
 * derived from the live custom-field config. Drives the Designer's editable
 * column list (every column shown so prices/sqft can be toggled even where a
 * given section wouldn't otherwise render them).
 */
export function defaultColumnsForScope(
  scope: ColumnLayoutScope,
  config: CustomFieldColumnConfig[] | undefined,
): ColumnMeta[] {
  const ctx: RenderCtx = { currency: 'USD', showPrices: true, getFields: () => [] };
  let cols: RenderColumn[];
  // startLineNo=1 so the # column appears as a Designer candidate (BOM only).
  if (scope === 'bom') cols = buildBomTable([], true, ctx, config, undefined, 1).columns;
  else if (scope === 'placementPanels') cols = buildPlacementTable([], true, ctx, config).columns;
  else cols = buildPlacementTable([], false, ctx, config).columns;
  return cols.map((c) => ({ layoutId: c.layoutId, heading: c.heading, kind: c.kind, align: c.align }));
}

/** SKU columns shared by BOM and placement, with table-specific classNames. */
function skuRenderColumns(skuColumns: SkuColumn[], thClass: string, tdClass: string): RenderColumn[] {
  return skuColumns.map((c) => ({
    key: c.key,
    layoutId: c.isBuiltIn ? 'sku' : `field:${c.key}`,
    heading: c.heading,
    kind: 'sku' as const,
    align: 'left' as const,
    thClassName: thClass,
    tdClassName: tdClass,
    widthHint: 1.4,
    ...VISIBLE_ALL,
    cell: (row: MaterialRow, ctx: RenderCtx) => skuCell(c, row, ctx),
  }));
}

/** Apply an item's overrides (heading/align/visibility) to a column. */
function decorateColumn(col: RenderColumn, item: ColumnLayoutItem): RenderColumn {
  return {
    ...col,
    heading: item.headingOverride && item.headingOverride.trim() !== '' ? item.headingOverride : col.heading,
    align: item.align ?? col.align,
    visibleBrowser: item.visibleBrowser,
    visiblePrint: item.visiblePrint,
    visiblePdf: item.visiblePdf,
  };
}

/** Build a synthetic barcode RenderColumn from a layout item's barcode config. */
function makeBarcodeColumn(item: ColumnLayoutItem): RenderColumn {
  const cfg = item.barcode!;
  return {
    key: item.id,
    layoutId: item.id,
    heading: tr('results:columns.barcode'),
    kind: 'barcode',
    align: 'center',
    thClassName: 'text-center',
    tdClassName: 'text-center',
    widthHint: 1.8,
    ...VISIBLE_ALL,
    cell: (row, ctx) => {
      const value = cfg.sourceFieldKey === 'productNumber'
        ? row.productNumber
        : CODE_SOURCE_KEYS.has(cfg.sourceFieldKey)
          ? resolveProductCode(cfg.sourceFieldKey, row.productID)
          : (ctx.getFields(row.productID).find((f) => f.fieldKey === cfg.sourceFieldKey)?.value ?? '');
      if (!value) {
        const label = cfg.sourceFieldKey === 'productNumber'
          ? tr('results:columns.productNumber')
          : (BUILTIN_CODE_SOURCES.find((c) => c.key === cfg.sourceFieldKey)?.label ?? cfg.sourceFieldKey);
        return { type: 'text', text: tr('results:table.codeNotSet', { label }) };
      }
      return {
        type: 'barcode',
        src: buildBarcodeUrl(value, cfg.symbology, 'svg', cfg.heightPx),
        pngUrl: buildBarcodeUrl(value, cfg.symbology, 'png', cfg.heightPx),
        alt: value,
        heightPx: cfg.heightPx,
      };
    },
  };
}

/**
 * Apply a saved ColumnLayout to default columns: reorder, relabel, realign, set
 * per-output visibility, and synthesize admin-added barcode columns
 * (id 'barcode:*'). Default columns absent from the layout keep their defaults
 * and are appended after laid-out ones (so newly-added fields still show). With
 * no layout, returns the columns unchanged (today's behavior).
 */
function applyLayout(columns: RenderColumn[], layout?: ColumnLayout): RenderColumn[] {
  if (!layout || !layout.items || layout.items.length === 0) return columns;
  const byId = new Map(columns.map((c) => [c.layoutId, c]));
  const used = new Set<string>();
  const ordered: { col: RenderColumn; order: number }[] = [];

  layout.items.forEach((item) => {
    if (item.barcode && item.id.startsWith('barcode:')) {
      ordered.push({ col: decorateColumn(makeBarcodeColumn(item), item), order: item.order });
      return;
    }
    const base = byId.get(item.id);
    if (base) {
      ordered.push({ col: decorateColumn(base, item), order: item.order });
      used.add(item.id);
    }
  });

  // Append default columns not referenced by the layout (e.g. newly-added fields).
  columns.forEach((col, index) => {
    if (!used.has(col.layoutId)) ordered.push({ col, order: 10000 + index });
  });

  return ordered
    .map((d, i) => ({ ...d, i }))
    .sort((a, b) => a.order - b.order || a.i - b.i)
    .map((d) => d.col);
}

/**
 * Build the BOM table model for one material section.
 * Columns: [SKU…] Product, Qty, ☑, Linear Ft, [Total Sqft if panel],
 * [Unit Price, Total if showPrices]. Footer = section totals.
 *
 * startLineNo (1-based) adds the line-item # column, numbering rows
 * startLineNo..startLineNo+rows-1 — the caller threads a running counter so
 * numbering is continuous across sections (BOM only; placement tables never
 * pass it).
 */
export function buildBomTable(
  items: MaterialItem[],
  isPanel: boolean,
  ctx: RenderCtx,
  config = undefined as Parameters<typeof buildSkuColumns>[0],
  layout?: ColumnLayout,
  startLineNo?: number,
): RenderTableModel {
  const skuColumns = buildSkuColumns(config, 'bom');
  // Row identity → line number (rows are the items array, passed by reference).
  const lineNos = new Map<MaterialItem, number>(
    startLineNo === undefined ? [] : items.map((it, i) => [it, startLineNo + i]),
  );
  const lineNoCol: RenderColumn | null = startLineNo === undefined ? null : {
    key: '__lineNo__', layoutId: 'builtin:lineNo', heading: tr('results:columns.lineNo'), kind: 'lineNo', align: 'center',
    thClassName: 'text-center col-line-no', tdClassName: 'text-center col-line-no', widthHint: 0.4, ...VISIBLE_ALL,
    cell: (row) => ({ type: 'text', text: String(lineNos.get(row) ?? '') }),
  };
  const columns: RenderColumn[] = [
    ...(lineNoCol ? [lineNoCol] : []),
    ...skuRenderColumns(skuColumns, 'sku-cell', 'sku-cell'),
    {
      key: '__product__', layoutId: 'builtin:product', heading: tr('results:columns.product'), kind: 'product', align: 'left',
      tdClassName: 'product-name-cell', widthHint: 2.4, ...VISIBLE_ALL,
      cell: (row) => ({ type: 'text', text: row.name }),
    },
    {
      key: '__qty__', layoutId: 'builtin:qty', heading: tr('results:columns.qty'), kind: 'qty', align: 'center',
      thClassName: 'text-center', tdClassName: 'text-center qty-cell', widthHint: 0.7, ...VISIBLE_ALL,
      cell: (row) => ({ type: 'text', text: String(row.quantity) }),
    },
    {
      key: '__check__', layoutId: 'builtin:checkbox', heading: '', kind: 'checkbox', align: 'center', headingIcon: 'checked',
      thClassName: 'text-center col-check', tdClassName: 'text-center col-check', widthHint: 0.6, ...VISIBLE_ALL,
      cell: () => ({ type: 'checkbox', checked: false }),
    },
    {
      key: '__linearFt__', layoutId: 'builtin:linearFt', heading: tr('results:columns.linearFt'), kind: 'linearFt', align: 'center',
      thClassName: 'text-center', tdClassName: 'text-center', widthHint: 0.9, ...VISIBLE_ALL,
      cell: (row) => ({ type: 'text', text: linearFtText(row) }),
    },
  ];

  if (isPanel) {
    columns.push({
      key: '__totalSqft__', layoutId: 'builtin:totalSqft', heading: tr('results:columns.totalSqft'), kind: 'totalSqft', align: 'center',
      thClassName: 'text-center', tdClassName: 'text-center', widthHint: 0.9, ...VISIBLE_ALL,
      cell: (row) => ({ type: 'text', text: totalSqftText(row) }),
    });
  }

  if (ctx.showPrices) {
    columns.push(
      {
        key: '__unitPrice__', layoutId: 'builtin:unitPrice', heading: tr('results:columns.unitPrice'), kind: 'unitPrice', align: 'right',
        thClassName: 'text-end', tdClassName: 'text-end', widthHint: 1, ...VISIBLE_ALL,
        cell: (row, c) => ({ type: 'text', text: unitPriceText(row.productID, c.currency) }),
      },
      {
        key: '__total__', layoutId: 'builtin:total', heading: tr('results:columns.total'), kind: 'total', align: 'right',
        thClassName: 'text-end', tdClassName: 'text-end total-cell', widthHint: 1, ...VISIBLE_ALL,
        cell: (row, c) => ({ type: 'text', text: totalPriceText(row.productID, row.quantity, c.currency) }),
      },
    );
  }

  // No special-casing of the # column's position: it is a normal builtin
  // column (default position: table-leading, before the SKU columns) and
  // saved layouts govern it like any other column via 'builtin:lineNo'.
  // Layouts saved before the column existed append it at the far right until
  // they are updated in the Column Layout Designer — the canonical way column
  // changes propagate to retailer variants.
  const finalCols = applyLayout(columns, layout);
  return { tableClassName: 'materials-table', columns: finalCols, rows: items, footer: buildBomFooter(finalCols, items, isPanel), analyticsSection: 'bom' };
}

/** Section totals row aligned to the BOM columns (mirrors the legacy tfoot). */
function buildBomFooter(
  columns: RenderColumn[],
  items: MaterialItem[],
  isPanel: boolean,
): RenderFooterCell[] {
  const totals = items.reduce(
    (acc, item) => ({
      linerFeet: acc.linerFeet + item.lengthFeet * item.quantity,
      totalSqft: isPanel ? acc.totalSqft + item.sqft * item.quantity : acc.totalSqft,
      totalQty: acc.totalQty + item.quantity,
    }),
    { linerFeet: 0, totalSqft: 0, totalQty: 0 },
  );

  // `columns` already reflects isPanel/showPrices, so a 1:1 map is correct.
  return columns.map((col): RenderFooterCell => {
    switch (col.kind) {
      case 'product':
        return { cell: { type: 'text', text: tr('results:table.sectionTotal') } };
      case 'qty':
        return { cell: { type: 'text', text: String(totals.totalQty) }, className: 'text-center' };
      case 'checkbox':
        return { cell: { type: 'empty' }, className: 'col-check' };
      case 'linearFt':
        return { cell: { type: 'text', text: totals.linerFeet.toLocaleString() }, className: 'text-center' };
      case 'totalSqft':
        return {
          cell: { type: 'text', text: totals.totalSqft.toLocaleString(undefined, { maximumFractionDigits: 0 }) },
          className: 'text-center',
        };
      default:
        // SKU, unit price, total → empty cell (matches legacy)
        return { cell: { type: 'empty' } };
    }
  });
}

/**
 * Build a placement table model (panels or trims). Panels include Total Sqft;
 * trims do not. No footer (matches legacy placement output).
 */
export function buildPlacementTable(
  items: MaterialItem[],
  withSqft: boolean,
  ctx: RenderCtx,
  config = undefined as Parameters<typeof buildSkuColumns>[0],
  layout?: ColumnLayout,
): RenderTableModel {
  const skuColumns = buildSkuColumns(config, 'placement');
  const columns: RenderColumn[] = [
    ...skuRenderColumns(skuColumns, 'col-sku', 'col-sku'),
    {
      key: '__product__', layoutId: 'builtin:product', heading: tr('results:columns.product'), kind: 'product', align: 'left',
      thClassName: 'col-product', widthHint: 2.4, ...VISIBLE_ALL,
      cell: (row) => ({ type: 'text', text: row.name }),
    },
    {
      key: '__qty__', layoutId: 'builtin:qty', heading: tr('results:columns.qty'), kind: 'qty', align: 'center',
      thClassName: 'col-qty text-center', tdClassName: 'text-center', widthHint: 0.7, ...VISIBLE_ALL,
      cell: (row) => ({ type: 'text', text: String(row.quantity) }),
    },
    {
      key: '__check__', layoutId: 'builtin:checkbox', heading: '', kind: 'checkbox', align: 'center', headingIcon: 'checked',
      thClassName: 'col-check text-center', tdClassName: 'text-center col-check', widthHint: 0.6, ...VISIBLE_ALL,
      cell: () => ({ type: 'checkbox', checked: false }),
    },
    {
      key: '__linearFt__', layoutId: 'builtin:linearFt', heading: tr('results:columns.linearFt'), kind: 'linearFt', align: 'center',
      thClassName: 'col-linear-ft text-center', tdClassName: 'text-center', widthHint: 0.9, ...VISIBLE_ALL,
      cell: (row) => ({ type: 'text', text: linearFtText(row) }),
    },
  ];

  if (withSqft) {
    columns.push({
      key: '__totalSqft__', layoutId: 'builtin:totalSqft', heading: tr('results:columns.totalSqft'), kind: 'totalSqft', align: 'center',
      thClassName: 'col-sqft text-center', tdClassName: 'text-center', widthHint: 0.9, ...VISIBLE_ALL,
      cell: (row) => ({ type: 'text', text: totalSqftText(row) }),
    });
  }

  return { tableClassName: 'placement-table', columns: applyLayout(columns, layout), rows: items, analyticsSection: 'placement_guide' };
}
